import { isPlainObject } from 'lodash';
import { KeyIteratee } from '@core/lib/fp/types';

type Value = Record<string, any>;

class KeysIterator {
  constructor(private iteratee: KeyIteratee, private excludedKeys: string[] | undefined) {}

  public execute = (obj: any): any => {
    if (this.isValidValue(obj)) {
      return this.deepMapKeys(obj);
    }
    return obj;
  }

  private isValidValue = (obj: any) => Array.isArray(obj) || isPlainObject(obj);

  private deepMapKeys = (obj: Value) => {
    if (Array.isArray(obj)) {
      return this.iterateArray(obj);
    }

    return this.iteratePlainObject(obj);
  };

  private iteratePlainObject = (obj: Value): Value =>
    Object.entries(obj)
      .map((([key, value]) => {
        if (this.excludedKeys && this.excludedKeys.includes(key)) {
          return [key, value];
        }

        return [this.iteratee(key), this.getNextValue(value)];
      }).bind(this))
      .reduce(
        (result, [key, value]) => ({
          ...result,
          [key]: value,
        }),
        {},
      );

  private iterateArray = (array: Value | Value[]): Value[] =>  array.map(this.deepMapKeys);

  private getNextValue = (value: any): any => {
    if (isPlainObject(value)) {
      return this.deepMapKeys(value);
    }
    if (Array.isArray(value)) {
      return value.map(this.getNextValue);
    }
    return value;
  };
}

export const deepMapKeys = (iteratee: KeyIteratee, excludedKeys?: string[]) => {
  const iterator = new KeysIterator(iteratee, excludedKeys);
  return (obj: any) => iterator.execute(obj);
};

